// What's new, in the clinic's language.
//
// Samuel's standing rule, 2026-08-25: put a version number in the corner, and
// clicking it shows everything that went into that release, written so anyone
// at Twin City can read it.
//
// HOW TO VERSION (MAJOR.MINOR.PATCH):
//
//   PATCH  1.0.0 -> 1.0.1   a small update. A fix, a tidy, one screen behaving
//                           better. Nothing new to learn.
//   MINOR  1.0.0 -> 1.1.0   a batch of real features. Something the clinic
//                           could not do yesterday and can do today.
//   MAJOR  the launch, and any later release big enough that Samuel names the
//                           number himself. After 1.0.0 the counters simply
//                           keep climbing - 1.21.20 is an ordinary version,
//                           three separate counters, NOT one-point-two-one.
//
// EVERY PUSH IS A RELEASE. Commits are not. A push with no bump is not
// finished. How much to bump is Samuel's call; the ship gate only enforces
// that the number goes up.
//
// ADDING A RELEASE: put the new entry at the TOP of releases.json. Write each
// line as something a Twin City staff member would recognise from their own
// day - name the screen and what changed for them. Never a ticket number,
// never a table name, never "refactored", never a figure. If a line cannot be
// written that way it belongs in the commit message, not here.

import releasesJson from './releases.json'

export type ChangeKind = 'added' | 'improved' | 'fixed'

export interface Change {
  kind: ChangeKind
  /** One plain sentence. What can they do now that they could not before? */
  text: string
  /** Request-tracker row numbers this line delivered, e.g. ["79"]. Joins the roadmap. */
  refs?: string[]
}

export interface Release {
  /** MAJOR.MINOR.PATCH */
  version: string
  /** ISO date the release was cut. */
  date: string
  /** One line summarising the release, shown beside the version. */
  headline: string
  changes: Change[]
}

export const CHANGE_KIND_LABELS: Record<ChangeKind, string> = {
  added: 'New',
  improved: 'Improved',
  fixed: 'Fixed',
}

// Newest first. The first entry is what the badge shows.
//
// THE DATA LIVES IN releases.json, not here. One file is read by the app (this
// module), by scripts/release.mjs which seeds it into app_releases, and by
// scripts/sync-roadmap.mjs which stamps each roadmap row with the version that
// shipped it. Three readers, one source, so the badge, the database and the
// client roadmap can never disagree about what went out.
//
// Each change may carry `refs`: the request-tracker row numbers it delivered.
// They are the join to /roadmap-35 and are NEVER shown to staff; `text` is the
// only thing the badge renders.
export const RELEASES: Release[] = releasesJson as Release[]

export const CURRENT_VERSION = RELEASES[0]?.version ?? '0.0.0'

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/

export function isValidVersion(version: string): boolean {
  return SEMVER.test(version.trim())
}

/**
 * Sorts by MAJOR, then MINOR, then PATCH - as three separate numbers.
 * 1.21.20 is NEWER than 1.3.0, which string sorting gets wrong.
 */
export function compareVersions(a: string, b: string): number {
  const pa = a.trim().match(SEMVER)
  const pb = b.trim().match(SEMVER)
  if (!pa || !pb) return 0
  for (let i = 1; i <= 3; i += 1) {
    const diff = Number(pa[i]) - Number(pb[i])
    if (diff !== 0) return diff
  }
  return 0
}

/** Releases newest first, whatever order the array happens to be written in. */
export function releasesNewestFirst(releases: Release[] = RELEASES): Release[] {
  return [...releases].sort((x, y) => compareVersions(y.version, x.version))
}

export function releaseFor(version: string, releases: Release[] = RELEASES): Release | undefined {
  return releases.find((r) => r.version === version)
}

/** Group a release's lines so the panel shows New / Improved / Fixed together. */
export function groupChanges(release: Release): Array<{ kind: ChangeKind; items: string[] }> {
  const order: ChangeKind[] = ['added', 'improved', 'fixed']
  return order
    .map((kind) => ({ kind, items: release.changes.filter((c) => c.kind === kind).map((c) => c.text) }))
    .filter((group) => group.items.length > 0)
}

/** One navigation entry per major.minor, with every patch newest first. */
export function groupReleaseLines(releases: Release[] = RELEASES): Array<{ line: string; releases: Release[] }> {
  const groups = new Map<string, Release[]>();
  for (const release of releasesNewestFirst(releases)) {
    const line = release.version.split('.').slice(0, 2).join('.') + '.x';
    const patches = groups.get(line) ?? [];
    patches.push(release);
    groups.set(line, patches);
  }
  return Array.from(groups, ([line, patches]) => ({ line, releases: patches }));
}
